import { createSlice } from "@reduxjs/toolkit";
import { RootState } from "../../app/store";
import roles from "../../utils/roles";
import { User } from "../../api/models/user-model";
import { LicenseRestrictions } from "../../api/models/licence-model";
import {
  PropertySettingKey,
  propertySettings,
  propertySettingsDefaults,
} from "../../utils/propertySettings";

export interface UserState {
  user: User | null;
}

const getInitialUser = (): User | null => {
  try {
    const stored = localStorage.getItem("user");
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
};

const initialState: UserState = {
  user: getInitialUser(),
};

export const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    getUser: (state) => {
      const newState = { ...state };
      const currentUser = localStorage.getItem("user");
      if (currentUser) {
        newState.user = JSON.parse(currentUser);
      }
      return newState;
    },
    setUser: (state, action) => {
      const newState = { ...state };
      newState.user = action.payload;
      // remove when user info endpoint available
      localStorage.setItem("user", JSON.stringify(action.payload));
      return newState;
    },
    resetUser: (state) => {
      const newState = { ...state };
      newState.user = null;
      localStorage.removeItem("user");
      return newState;
    },
  },
});

export const { setUser, getUser, resetUser } = userSlice.actions;

export const selectUser = (state: RootState) => state?.user?.user;
export const selectUserRole = (state: RootState) =>
  roles[state?.user?.user?.role?.name.slice(0, -2) || ""];
export const selectUserRestrictions = (state: RootState) => {
  const role = state?.user?.user?.role?.name?.slice(0, -2);
  if (
    state?.user?.user?.license?.restrictions &&
    role &&
    role in state?.user?.user?.license?.restrictions
  ) {
    return state?.user?.user?.license?.restrictions[
      role as keyof LicenseRestrictions
    ];
  }

  return undefined;
};

export const selectHasUserPropertySetting =
  (setting: PropertySettingKey) =>
  (state: RootState): boolean => {
    const settings = state?.user?.user?.property?.settings as
      | Record<string, any>
      | undefined;

    const backendKey = propertySettings[setting];
    const defaultValue = propertySettingsDefaults[setting];

    if (!settings) return defaultValue;

    // si no existe la key, usa default
    if (!(backendKey in settings)) return defaultValue;

    // si existe, respeta su valor
    return settings[backendKey] === true;
  };

export default userSlice.reducer;
